import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Construction, Mail, Download, Trash2, Loader2,
  Eye, ShieldAlert, ShieldCheck, Save, Send, AlertTriangle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import MiniRichTextEditor from '@/components/MiniRichTextEditor';

export default function AdminMaintenance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notifyStatus, setNotifyStatus] = useState(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/');
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, n] = await Promise.all([
        api.get('/admin/maintenance'),
        api.get('/admin/maintenance/subscribers').catch(() => ({ data: { items: [], total: 0 } })),
        api.get('/admin/maintenance/notify-status').catch(() => ({ data: null })),
      ]);
      setCfg(s.data);
      setSubs(l.data.items || []);
      setNotifyStatus(n.data);
    } catch (err) {
      toast.error('Não foi possível carregar as definições.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const toggleEnabled = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/maintenance', { enabled: next });
      setCfg(data);
      toast.success(next ? 'Modo de manutenção ATIVADO. Os visitantes vão ver a página de manutenção.' : 'Modo de manutenção desativado.');
    } catch (err) {
      toast.error('Erro ao alterar o estado.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEmailCapture = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/maintenance', { email_capture_enabled: next });
      setCfg(data);
      toast.success(next ? 'Captura de email ativada.' : 'Captura de email desativada.');
    } catch (err) {
      toast.error('Erro ao alterar a captura de email.');
    } finally {
      setSaving(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/maintenance', {
        title: cfg.title,
        subtitle: cfg.subtitle,
        description: cfg.description,
        footer: cfg.footer,
        // Launch email fields — without these, the inputs for "assunto",
        // "título principal" and "primeiro parágrafo" appeared editable
        // but the values were never persisted server-side.
        launch_email_subject: cfg.launch_email_subject,
        launch_email_subject_intro: cfg.launch_email_subject_intro,
        launch_email_body_intro: cfg.launch_email_body_intro,
      });
      setCfg(data);
      toast.success('Conteúdo guardado.');
    } catch (err) {
      toast.error('Erro ao guardar o conteúdo.');
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const resp = await api.get('/admin/maintenance/subscribers/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `maintenance_subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao exportar.');
    } finally {
      setExporting(false);
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm('Eliminar este subscritor?')) return;
    try {
      await api.delete(`/admin/maintenance/subscribers/${id}`);
      setSubs((arr) => arr.filter((s) => s.id !== id));
      toast.success('Subscritor eliminado.');
    } catch {
      toast.error('Erro ao eliminar.');
    }
  };

  const sendNotifyAll = async (force = false) => {
    setNotifySending(true);
    setNotifyResult(null);
    try {
      const { data } = await api.post('/admin/maintenance/notify-all', { force });
      setNotifyResult(data);
      if (data.sent > 0) {
        toast.success(`Email enviado a ${data.sent} ${data.sent === 1 ? 'pessoa' : 'pessoas'}.`);
      } else if (data.total === 0) {
        toast.info('Não há novos subscritores para notificar.');
      }
      // Reload status + subscribers (notified_at flags updated)
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar os emails.');
    } finally {
      setNotifySending(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  const enabled = !!cfg.enabled;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 sm:pt-24 pb-24 sm:pb-12 px-4 sm:px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            data-testid="maintenance-back"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />Voltar ao painel
          </button>

          <header className="flex items-center gap-4 mb-6">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${enabled ? 'bg-amber-500/20' : 'bg-surface-light'}`}>
              <Construction className={`w-7 h-7 ${enabled ? 'text-amber-400' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Modo de Manutenção</h1>
              <p className="text-sm text-muted-foreground">Controla o acesso público à plataforma</p>
            </div>
          </header>

          {/* Master toggle */}
          <Card className={`mb-6 border-2 ${enabled ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-surface'}`}>
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
                <div className="flex items-start gap-3">
                  {enabled ? (
                    <ShieldAlert className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
                  ) : (
                    <ShieldCheck className="w-6 h-6 text-green-400 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <h2 className="font-bold text-lg text-foreground mb-1">
                      {enabled ? 'Manutenção ATIVA' : 'Plataforma online'}
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-md">
                      {enabled
                        ? 'Os visitantes vão ver a página de manutenção. Tu (administrador) continuas com acesso total.'
                        : 'A plataforma está acessível a todos os utilizadores. Ativa esta opção para fechar o acesso público.'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={toggleEnabled}
                  disabled={saving}
                  data-testid="maintenance-toggle"
                  className="data-[state=checked]:bg-amber-500"
                />
              </div>
              {enabled && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">
                    Visitantes bloqueados
                  </Badge>
                  <Badge className="bg-green-500/15 text-green-300 border-green-500/30">
                    Admin com acesso
                  </Badge>
                  <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30">
                    Webhooks ativos
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Content editor */}
          <Card className="mb-6 bg-surface border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Conteúdo da página</CardTitle>
              <CardDescription>O que os visitantes vão ler durante a manutenção.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-sm mb-2 block">Título</Label>
                <Input
                  id="title"
                  value={cfg.title || ''}
                  onChange={(e) => update({ title: e.target.value })}
                  data-testid="maintenance-input-title"
                  maxLength={300}
                />
              </div>
              <div>
                <Label htmlFor="subtitle" className="text-sm mb-2 block">Subtítulo</Label>
                <textarea
                  id="subtitle"
                  value={cfg.subtitle || ''}
                  onChange={(e) => update({ subtitle: e.target.value })}
                  data-testid="maintenance-input-subtitle"
                  rows={2}
                  maxLength={600}
                  className="w-full px-3 py-2 rounded-md bg-surface-light border border-border text-foreground text-sm focus:outline-none focus:border-ruby focus:ring-2 focus:ring-ruby/20"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-sm mb-2 block">Descrição</Label>
                <textarea
                  id="description"
                  value={cfg.description || ''}
                  onChange={(e) => update({ description: e.target.value })}
                  data-testid="maintenance-input-description"
                  rows={3}
                  maxLength={2000}
                  className="w-full px-3 py-2 rounded-md bg-surface-light border border-border text-foreground text-sm focus:outline-none focus:border-ruby focus:ring-2 focus:ring-ruby/20"
                />
              </div>
              <div>
                <Label htmlFor="footer" className="text-sm mb-2 block">Rodapé</Label>
                <Input
                  id="footer"
                  value={cfg.footer || ''}
                  onChange={(e) => update({ footer: e.target.value })}
                  data-testid="maintenance-input-footer"
                  maxLength={300}
                />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={saveContent}
                  disabled={saving}
                  data-testid="maintenance-save-content"
                  className="bg-ruby hover:bg-ruby-light text-white rounded-full"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar conteúdo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open('/?maintenance_preview=1', '_blank')}
                  className="rounded-full"
                >
                  <Eye className="w-4 h-4 mr-2" />Pré-visualizar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email capture */}
          <Card className="mb-6 bg-surface border-border">
            <CardContent className="p-5 sm:p-6 flex items-start justify-between gap-4 flex-col sm:flex-row">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">Captura de email</h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Mostrar um campo "Receber aviso de lançamento" para os visitantes deixarem o email.
                  </p>
                </div>
              </div>
              <Switch
                checked={!!cfg.email_capture_enabled}
                onCheckedChange={toggleEmailCapture}
                disabled={saving}
                data-testid="maintenance-email-toggle"
              />
            </CardContent>
          </Card>

          {/* Launch announcement */}
          <Card className="mb-6 bg-surface border-ruby/30">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Send className="w-5 h-5 text-ruby" />
                Aviso de lançamento
              </CardTitle>
              <CardDescription>
                Envia um email a todos os subscritores capturados a avisar que a plataforma já está online.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Counts */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="bg-surface-light rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="notify-total">{notifyStatus?.total_subscribers ?? 0}</p>
                </div>
                <div className="bg-surface-light rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Avisados</p>
                  <p className="text-2xl font-bold text-green-400" data-testid="notify-notified">{notifyStatus?.already_notified ?? 0}</p>
                </div>
                <div className="bg-surface-light rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Por avisar</p>
                  <p className="text-2xl font-bold text-ruby" data-testid="notify-pending">{notifyStatus?.pending ?? 0}</p>
                </div>
              </div>

              {/* Editable email content */}
              <details className="rounded-xl border border-border bg-surface-light/50 group" data-testid="notify-email-content">
                <summary className="cursor-pointer p-3 text-sm font-medium text-foreground flex items-center justify-between">
                  <span>Personalizar conteúdo do email</span>
                  <span className="text-xs text-muted-foreground group-open:hidden">Mostrar</span>
                  <span className="text-xs text-muted-foreground hidden group-open:inline">Esconder</span>
                </summary>
                <div className="p-3 pt-0 space-y-3">
                  <div>
                    <Label htmlFor="launch-subject" className="text-xs mb-1.5 block">Assunto do email</Label>
                    <Input
                      id="launch-subject"
                      value={cfg.launch_email_subject || ''}
                      onChange={(e) => update({ launch_email_subject: e.target.value })}
                      data-testid="launch-input-subject"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <Label htmlFor="launch-subject-intro" className="text-xs mb-1.5 block">Título principal do email</Label>
                    <Input
                      id="launch-subject-intro"
                      value={cfg.launch_email_subject_intro || ''}
                      onChange={(e) => update({ launch_email_subject_intro: e.target.value })}
                      data-testid="launch-input-subject-intro"
                      maxLength={300}
                    />
                  </div>
                  <div>
                    <Label htmlFor="launch-body" className="text-xs mb-1.5 block">
                      Primeiro parágrafo <span className="text-muted-foreground/70">— suporta negrito, itálico e links</span>
                    </Label>
                    <MiniRichTextEditor
                      value={cfg.launch_email_body_intro || ''}
                      onChange={(html) => update({ launch_email_body_intro: html })}
                      data-testid="launch-input-body"
                      placeholder="Escreve aqui o primeiro parágrafo do email…"
                      minHeight="120px"
                    />
                  </div>
                  <Button
                    onClick={saveContent}
                    disabled={saving}
                    size="sm"
                    data-testid="launch-save-email-content"
                    className="bg-ruby/20 hover:bg-ruby/30 text-ruby border border-ruby/40 rounded-full"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
                    Guardar conteúdo do email
                  </Button>
                </div>
              </details>

              {/* Result of last send (this session) */}
              {notifyResult && (
                <div className={`rounded-xl border p-3 text-sm ${notifyResult.failed > 0 ? 'border-amber-500/40 bg-amber-500/10 text-amber-200' : 'border-green-500/30 bg-green-500/10 text-green-200'}`} data-testid="notify-last-result">
                  Último envio nesta sessão: <strong>{notifyResult.sent}</strong> enviados, <strong>{notifyResult.failed}</strong> falhados de <strong>{notifyResult.total}</strong>.
                </div>
              )}

              {/* Trigger */}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => setNotifyOpen(true)}
                  disabled={notifySending || (notifyStatus?.pending ?? 0) === 0}
                  data-testid="notify-open"
                  className="bg-ruby hover:bg-ruby-light text-white rounded-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {(notifyStatus?.pending ?? 0) === 0 ? 'Sem subscritores por avisar' : `Avisar ${notifyStatus.pending} ${notifyStatus.pending === 1 ? 'subscritor' : 'subscritores'} agora`}
                </Button>
                {(notifyStatus?.already_notified ?? 0) > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => sendNotifyAll(true)}
                    disabled={notifySending}
                    data-testid="notify-force"
                    className="rounded-full text-xs"
                    title="Reenviar a todos, incluindo quem já recebeu"
                  >
                    {notifySending ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
                    Reenviar a todos ({notifyStatus.total_subscribers})
                  </Button>
                )}
              </div>

              {/* Last batch info */}
              {notifyStatus?.last_batch && (
                <p className="text-xs text-muted-foreground">
                  Último lote: {new Date(notifyStatus.last_batch.started_at).toLocaleString('pt-PT')} — {notifyStatus.last_batch.sent || 0} enviados, {notifyStatus.last_batch.failed || 0} falhados.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Subscribers list */}
          <Card className="bg-surface border-border">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" />
                  Emails capturados <span className="text-sm font-normal text-muted-foreground">({subs.length})</span>
                </CardTitle>
                <CardDescription>Visitantes a aguardar o lançamento</CardDescription>
              </div>
              {subs.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportCsv}
                  disabled={exporting}
                  data-testid="maintenance-export"
                  className="rounded-full"
                >
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  CSV
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {subs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="maintenance-empty">
                  Ainda não há subscritores. Aparecem aqui assim que alguém deixar o email.
                </p>
              ) : (
                <ul className="divide-y divide-border" data-testid="maintenance-subscribers-list">
                  {subs.map((s) => (
                    <li key={s.id} className="flex items-center justify-between py-3 gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{s.email}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.created_at ? new Date(s.created_at).toLocaleString('pt-PT') : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteSub(s.id)}
                        className="text-muted-foreground hover:text-red-400 transition-colors"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Confirmation dialog for bulk send */}
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="sm:max-w-md" data-testid="notify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-ruby">
              <Send className="w-5 h-5" />Avisar subscritores
            </DialogTitle>
            <DialogDescription>
              Vais enviar o email de lançamento a <strong className="text-foreground">{notifyStatus?.pending ?? 0}</strong> {(notifyStatus?.pending ?? 0) === 1 ? 'pessoa' : 'pessoas'} que ainda não foram avisadas.
              <br /><br />
              O envio é feito agora via Brevo. Quem já recebeu é ignorado. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-200 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Confirma que o conteúdo do email está como queres antes de avançar.</span>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setNotifyOpen(false)}
              disabled={notifySending}
              data-testid="notify-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => { await sendNotifyAll(false); setNotifyOpen(false); }}
              disabled={notifySending || (notifyStatus?.pending ?? 0) === 0}
              data-testid="notify-confirm"
              className="bg-ruby hover:bg-ruby-light text-white"
            >
              {notifySending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Enviar agora</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
